import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Pencil } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import {
  actualizarCondicion,
  type CondicionElemento,
} from '@/services/api/inventario.api';
import { getApiErrorMessage } from '@/services/api/client';
import { cn } from '@/lib/utils';
import { CONDICION_META, CONDICIONES } from '@/lib/condicion';

export interface EditarCondicionItem {
  id_item_maestro: string;
  serial_global: string;
  condicion: CondicionElemento | null;
}

interface Props {
  item: EditarCondicionItem | null;
  onOpenChange: (open: boolean) => void;
}

export function EditarCondicionDialog({ item, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [seleccionada, setSeleccionada] = useState<CondicionElemento | null>(
    item?.condicion ?? null,
  );

  const mutation = useMutation({
    mutationFn: (condicion: CondicionElemento) =>
      actualizarCondicion(item!.id_item_maestro, condicion),
    onSuccess: () => {
      toast.success('Condición actualizada');
      qc.invalidateQueries({ queryKey: ['inventario-items'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error('No se pudo actualizar la condición', {
        description: getApiErrorMessage(err),
      });
    },
  });

  function handleGuardar() {
    if (!seleccionada) return;
    mutation.mutate(seleccionada);
  }

  const puedeGuardar =
    !!seleccionada &&
    seleccionada !== item?.condicion &&
    !mutation.isPending;

  return (
    <Dialog open={!!item} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <Pencil className="h-4 w-4" />
            </span>
            Editar condición
          </DialogTitle>
          <DialogDescription>
            {item?.serial_global} — seleccioná el nuevo estado físico del elemento.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-1.5">
          {CONDICIONES.map((c) => {
            const meta = CONDICION_META[c];
            const Icon = meta.icon;
            const activa = seleccionada === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setSeleccionada(c)}
                className={cn(
                  'flex items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                  activa
                    ? 'border-primary-400 bg-primary-50/60 ring-1 ring-primary-200'
                    : 'border-border hover:bg-muted/50',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded-full border',
                    activa ? 'border-primary-600' : 'border-slate-300',
                  )}
                >
                  {activa && <span className="h-2 w-2 rounded-full bg-primary-600" />}
                </span>
                <Icon className={cn('h-4 w-4 shrink-0', meta.text)} />
                <span className="font-medium text-foreground">{meta.label}</span>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={!puedeGuardar}>
            {mutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Guardando...
              </>
            ) : (
              'Guardar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
